import React, { useState, useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, Check, FileText } from 'lucide-react';
import './TaxFormModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TaxFormModal = ({ assignment, currentUser, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({});
  const [signature, setSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sigCanvas = useRef(null);

  // Load saved progress when modal opens
  useEffect(() => {
    const savedProgressKey = `assignment_progress_${currentUser?.email}_${assignment.id}`;
    const savedProgress = localStorage.getItem(savedProgressKey);
    
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        setFormData(parsed.formData || {});
        setSignature(parsed.signature || '');
        console.log('✅ Loaded saved progress for', assignment.title);
      } catch (err) {
        console.error('Error loading saved progress:', err);
      }
    }
  }, [assignment.id, currentUser?.email]);

  // Save progress whenever form data changes
  useEffect(() => {
    if (Object.keys(formData).length > 0 || signature) {
      const savedProgressKey = `assignment_progress_${currentUser?.email}_${assignment.id}`;
      const progressData = {
        formData,
        signature,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(savedProgressKey, JSON.stringify(progressData));
    }
  }, [formData, signature, assignment.id, currentUser?.email]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignature('');
  };

  const saveSignature = () => {
    if (sigCanvas.current) {
      const dataURL = sigCanvas.current.toDataURL();
      setSignature(dataURL);
    }
  };

  const handleSubmit = async () => {
    if (!signature) {
      alert('Please provide your signature before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      const submission = {
        form_type: assignment.formType,
        form_title: assignment.title,
        employee_name: currentUser?.name || 'Employee',
        employee_email: currentUser?.email || '',
        form_data: formData,
        signature: signature,
        submission_date: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/email/submit-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submission)
      });

      if (!response.ok) throw new Error('Failed to submit form');

      const result = await response.json();
      alert(`Form submitted successfully to ${result.recipient}`);
      
      // Clear saved progress after successful submission
      const savedProgressKey = `assignment_progress_${currentUser?.email}_${assignment.id}`;
      localStorage.removeItem(savedProgressKey);
      
      onSubmit(assignment.id);
      onClose();

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // W-2 Form Fields
  const renderW2Form = () => (
    <div className="form-content">
      <h3><FileText size={20} /> W-2 Wage and Tax Statement 2026</h3>
      <p className="form-description">Please fill out all required fields for your W-2 form.</p>

      <div className="form-grid">
        <div className="form-field full-width">
          <label>a. Employee's Social Security Number *</label>
          <input 
            type="text" 
            placeholder="XXX-XX-XXXX"
            value={formData.employee_ssn || ''}
            onChange={(e) => handleInputChange('employee_ssn', e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label>b. Employer Identification Number (EIN)</label>
          <input 
            type="text" 
            value="22-2222"
            disabled
            className="disabled-field"
          />
        </div>

        <div className="form-field">
          <label>c. Employer's Name</label>
          <input 
            type="text" 
            value="AG&WESTERN Rail Inc."
            disabled
            className="disabled-field"
          />
        </div>

        <div className="form-field full-width">
          <label>e. Employee's First Name and Initial, Last Name *</label>
          <input 
            type="text" 
            placeholder="Full Name"
            value={formData.employee_full_name || currentUser?.name || ''}
            onChange={(e) => handleInputChange('employee_full_name', e.target.value)}
            required
          />
        </div>

        <div className="form-field full-width">
          <label>f. Employee's Address and ZIP Code *</label>
          <input 
            type="text" 
            placeholder="Street Address, City, State, ZIP"
            value={formData.employee_address || ''}
            onChange={(e) => handleInputChange('employee_address', e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label>1. Wages, Tips, Other Compensation</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.wages || ''}
            onChange={(e) => handleInputChange('wages', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>2. Federal Income Tax Withheld</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.federal_tax_withheld || ''}
            onChange={(e) => handleInputChange('federal_tax_withheld', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>3. Social Security Wages</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.ss_wages || ''}
            onChange={(e) => handleInputChange('ss_wages', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>4. Social Security Tax Withheld</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.ss_tax_withheld || ''}
            onChange={(e) => handleInputChange('ss_tax_withheld', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>5. Medicare Wages and Tips</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.medicare_wages || ''}
            onChange={(e) => handleInputChange('medicare_wages', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>6. Medicare Tax Withheld</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.medicare_tax_withheld || ''}
            onChange={(e) => handleInputChange('medicare_tax_withheld', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // W-4 Form Fields
  const renderW4Form = () => (
    <div className="form-content">
      <h3><FileText size={20} /> W-4 Employee's Withholding Certificate 2026</h3>
      <p className="form-description">Complete this form so your employer can withhold the correct federal income tax.</p>

      <div className="form-grid">
        <div className="form-field full-width">
          <label>First Name and Middle Initial, Last Name *</label>
          <input 
            type="text" 
            placeholder="Full Name"
            value={formData.full_name || currentUser?.name || ''}
            onChange={(e) => handleInputChange('full_name', e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label>Social Security Number *</label>
          <input 
            type="text" 
            placeholder="XXX-XX-XXXX"
            value={formData.ssn || ''}
            onChange={(e) => handleInputChange('ssn', e.target.value)}
            required
          />
        </div>

        <div className="form-field full-width">
          <label>Address *</label>
          <input 
            type="text" 
            placeholder="Street Address"
            value={formData.address || ''}
            onChange={(e) => handleInputChange('address', e.target.value)}
            required
          />
        </div>

        <div className="form-field full-width">
          <label>City, State, and ZIP Code *</label>
          <input 
            type="text" 
            placeholder="City, State, ZIP"
            value={formData.city_state_zip || ''}
            onChange={(e) => handleInputChange('city_state_zip', e.target.value)}
            required
          />
        </div>

        <div className="form-field full-width">
          <label>Filing Status *</label>
          <select 
            value={formData.filing_status || ''}
            onChange={(e) => handleInputChange('filing_status', e.target.value)}
            required
          >
            <option value="">Select Filing Status</option>
            <option value="single">Single or Married filing separately</option>
            <option value="married">Married filing jointly or Qualifying surviving spouse</option>
            <option value="head">Head of household</option>
          </select>
        </div>

        <div className="form-field">
          <label>Number of Qualifying Children (Step 3)</label>
          <input 
            type="number" 
            placeholder="0"
            min="0"
            value={formData.num_children || ''}
            onChange={(e) => handleInputChange('num_children', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>Number of Other Dependents (Step 3)</label>
          <input 
            type="number" 
            placeholder="0"
            min="0"
            value={formData.num_dependents || ''}
            onChange={(e) => handleInputChange('num_dependents', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>Extra Withholding (Step 4c)</label>
          <input 
            type="number" 
            placeholder="0.00"
            value={formData.extra_withholding || ''}
            onChange={(e) => handleInputChange('extra_withholding', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{assignment.title}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {assignment.formType === 'w2' && renderW2Form()}
          {assignment.formType === 'w4' && renderW4Form()}

          {/* Signature Section */}
          <div className="signature-section">
            <h4>Electronic Signature *</h4>
            <p>By signing below, I certify that the information provided is accurate and complete.</p>
            
            <div className="signature-canvas-container">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  className: 'signature-canvas',
                  width: 500,
                  height: 150
                }}
                onEnd={saveSignature}
              />
            </div>
            
            <button className="clear-signature-btn" onClick={clearSignature}>
              Clear Signature
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="submit-btn" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Check size={18} />
            {isSubmitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxFormModal;
